import { caseOpened } from "../../utils/genericUtils";
import { useModalContext } from "../../context/ModalContext";
import { useSelector } from "react-redux";
import { useState } from "react";
import { RejectTnsModalContent } from "./RejectTnsModalContent";
import { AcceptTnsModalContent } from "./AcceptTnsModalContent";
import { FaInfoCircle } from "react-icons/fa";
import { useRef } from "react";
import { Button, IconButton, Skeleton, Tooltip } from "@mui/material";
import { useIsAdminUser } from "../../hooks/useIsAdminUser";
import SingularCase from "../../model/store/singularCase";
import { AppState } from "../../store";
import React from "react";
import { TnResult } from "../../model/store/tnResult";
import { CaseStatus } from "../../model/store/caseStatus";
import { Typography } from "@mui/material";

const resultCodeToCssClass = {
  ACCEPTED: "accepted",
  ERROR_CHANGED_PROVISIONERS: "error error-changed-provisioners",
  ERROR_GENERIC: "error error-generic",
  REJECTED: "rejected",
};

interface RequestedTnsProps {
  currentCase: SingularCase;
}

const RequestedTns = ({ currentCase }: RequestedTnsProps) => {
  const { displayModal } = useModalContext();
  const pid = useSelector((state: AppState) => state.auth.pid);
  const disabled = currentCase.openedBy === pid || !caseOpened(currentCase);
  const isAdmin = useIsAdminUser()();
  const isLoading = useSelector(
    (state: AppState) => state.conflictCase.isLoading
  );

  const [selectedTns, setSelectedTns] = useState<string[]>([]);
  const requestedTns = currentCase.tns;
  const selectAllChecked =
    selectedTns.length ===
      requestedTns
        .map((tnObj) => {
          if (!tnObj.responded) {
            return tnObj.tn;
          }
        })
        .filter((tnOrNull) => tnOrNull).length && caseOpened(currentCase);

  /**
   * If "Select All" is already checked, this will deselect all numbers. If "Select All" isn't checked, this will select all Tns.
   * Update selectedTns state.
   *
   * @param {Object: Event} e The event object created by the user click of "Select All" button.
   */
  const selectDeselectAllTns = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedTns(
        requestedTns
          .map((tnObj) => {
            if (!tnObj.responded) {
              return tnObj.tn;
            }
          })
          .filter((tnOrNull) => tnOrNull) as string[]
      );
    } else {
      setSelectedTns([]);
    }
  };

  /**
   * Select or deselect the number that was just clicked. Update selectedTns state.
   *
   * @param {Object: Event} e The event object created by the user click of a specific TN
   */
  const clickTn = (e: React.ChangeEvent<HTMLInputElement>) => {
    let tn = e.target.id;
    let tnIndex = selectedTns.findIndex((potentialTn) => tn === potentialTn);
    if (tnIndex === -1) {
      setSelectedTns((selectedTns) => [...selectedTns, tn]);
    } else {
      setSelectedTns((selectedTns) => {
        if (tnIndex < selectedTns.length && selectedTns[tnIndex] === tn) {
          //Catching weird bug where this function is ran twice
          selectedTns.splice(tnIndex, 1);
        }
        return [...selectedTns]; //For some reason, needed to destructure to render correctly
      });
    }
  };

  /**
   * Opens Accept TNs modal with AcceptTnsModalContent component, with validations
   */
  const openAcceptTnsModal = () => {
    if (pid === currentCase.openedBy) {
      displayModal(
        <p>You cannot accept TNs in a case you opened</p>,
        "Oops!",
        { width: "50%" },
        true
      );
    } else if (!caseOpened(currentCase)) {
      displayModal(
        <p>You cannot accept TNs in a closed case!</p>,
        "Oops!",
        { width: "50%" },
        true
      );
    } else {
      displayModal(
        <AcceptTnsModalContent
          tns={selectedTns}
          provisioner={currentCase.openedBy}
          caseId={currentCase.caseId}
          setSelectedTns={setSelectedTns}
        />,
        "Accept TNs",
        { width: "50%" },
        false
      );
    }
  };

  /**
   * Opens Reject TNs modal with RejectTnsModalContent component, with validations
   */
  const openRejectTnsModal = () => {
    if (pid === currentCase.openedBy) {
      displayModal(
        <p>You cannot reject TNs in a case you opened</p>,
        "Oops!",
        { width: "50%" },
        true
      );
    } else if (currentCase.status === CaseStatus.CLOSED) {
      displayModal(
        <p>You cannot reject TNs in a closed case!</p>,
        "Oops!",
        { width: "50%" },
        true
      );
    } else {
      displayModal(
        <RejectTnsModalContent
          tns={selectedTns}
          provisioner={currentCase.openedBy}
          caseNnidType={currentCase.nnidType}
          caseId={currentCase.caseId}
          setSelectedTns={setSelectedTns}
        />,
        "Reject TNs",
        { width: "50%" },
        false
      );
    }
  };

  if (!requestedTns) {
    return <div>There are no TNs associated with this case...</div>;
  }

  if (isLoading) {
    return <RequestedTnsIsLoading />;
  }

  return (
    <div className="requested-header">
      <div className="requested-tns-header tn">
        <input
          type="checkbox"
          id="select-all"
          onChange={selectDeselectAllTns}
          disabled={disabled}
          checked={selectAllChecked}
        />
        <label htmlFor="select-all" className="fixed-width-text">
          {!selectAllChecked ? "Select All" : "Deselect All"}
        </label>
      </div>
      <div className="requested-tns">
        {requestedTns.map((tn) => {
          return (
            <div
              className={
                "tn " + (tn.responded ? resultCodeToCssClass[tn.result!] : "")
              }
              key={tn.tn as React.Key}
            >
              <input
                type="checkbox"
                id={tn.tn}
                onChange={(e) => {
                  if (!tn.responded) {
                    clickTn(e);
                  }
                }}
                disabled={tn.responded || disabled}
                checked={selectedTns.includes(tn.tn)}
              />
              <span className="tn-container">
                <label htmlFor={tn.tn} className="fixed-width-text">
                  {tn.tn}
                </label>
                {tn.result === "ERROR_CHANGED_PROVISIONERS" && (
                  <>
                    <Tooltip
                      title={
                        <Typography>
                          Unfortunately, this TN has moved to a different PID.
                          You will need to open a new case
                        </Typography>
                      }
                    >
                      <IconButton size="small">
                        <FaInfoCircle size={"16px"} />
                      </IconButton>
                    </Tooltip>
                    {/***/}
                  </>
                )}
                {tn.result === "ERROR_GENERIC" && (
                  <>
                    <Tooltip
                      title={
                        <Typography>
                          While the provisioner accepted this TN, there was an
                          unexpected error in releasing it. Please reach out to
                          netnumber for further assistance
                        </Typography>
                      }
                    >
                      <IconButton>
                        <FaInfoCircle />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className="requested-tns-controls">
        {!isAdmin && (
          <>
            <Button
              className={disabled ? "disabled" : "success"}
              disabled={disabled}
              variant="contained"
              color="success"
              onClick={openAcceptTnsModal}
            >
              Accept
            </Button>
            <Button
              onClick={openRejectTnsModal}
              disabled={disabled}
              variant="contained"
              color="error"
              className={disabled ? "disabled" : "danger"}
            >
              Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const RequestedTnsIsLoading = () => {
  const isAdmin = useIsAdminUser()();

  return (
    <>
      <div className="requested-tns">
        {Array(4)
          .fill(null)
          .map(() => {
            return (
              <div className="tn">
                <Skeleton
                  variant="rectangular"
                  sx={{
                    fontSize: "1.5rem",
                  }}
                />
              </div>
            );
          })}
      </div>
      <div className="requested-tns-controls">
        {!isAdmin && (
          <>
            <Button disabled={true} variant="contained" color="success">
              Accept
            </Button>
            <Button disabled={true} variant="contained" color="error">
              Reject
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export { RequestedTns };
